import { forwardRef } from "react";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";

/**
 * One button in a region's hover chrome — sound, loop, link, mirror.
 *
 * ONE FUNCTION BECAUSE THEY MUST AGREE. This class string used to be copy-pasted into
 * three files (RegionHoverChrome, RegionLinkButton, RegionMirrorButton), which is fine
 * right up until one of them changes: giving only the first a bigger touch target grew
 * sound and loop on a phone and left link and mirror at their old size, in the same row,
 * a pixel apart. Chrome that sits shoulder to shoulder cannot be styled in three places.
 *
 * `pointer-coarse:p-2.5` is the finger's share. At `p-1` a 14px glyph makes a 22px button,
 * and useTileScale then multiplies it by as little as 0.7 on the slivers a dense wall makes
 * of a phone — about 15px of target. The hook's floor is not the culprit; its doc already
 * chose 0.7 so that "chrome smaller than that stops being a target you can hit". The BASE
 * was never sized for a finger.
 *
 * COARSE, never fine — the polarity rule from UsePhoneCard: a context that reports neither
 * must fall through to the mouse-sized control, not to an untappable one.
 */
export function regionChromeButton(active: boolean): string {
  return cx(
    "rounded-md p-1 pointer-coarse:p-2.5 backdrop-blur-sm transition-colors",
    active
      ? "bg-[var(--color-ink)]/70 text-[var(--color-accent-soft)] hover:bg-[var(--color-ink)]/90"
      : "bg-[var(--color-ink)]/45 text-zinc-300 opacity-70 hover:opacity-100",
  );
}

/** Tiny className joiner (no clsx dependency). */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

type Variant = "primary" | "ghost" | "danger" | "subtle";
type Size = "sm" | "md";

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-soft)] border-transparent",
  ghost:
    "bg-transparent text-zinc-200 hover:bg-[var(--color-panel-2)] border-[var(--color-edge)]",
  subtle:
    "bg-[var(--color-panel-2)] text-zinc-200 hover:bg-[var(--color-edge)] border-[var(--color-edge)]",
  danger:
    "bg-transparent text-red-300 hover:bg-red-500/15 border-red-500/40",
};

const SIZE: Record<Size, string> = {
  sm: "px-2.5 py-1 text-xs rounded-md gap-1.5",
  md: "px-3.5 py-2 text-sm rounded-lg gap-2",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

/** Button's look as a class string, for a router Link that should read as a button. */
export function buttonClass(variant: Variant = "subtle", size: Size = "md", className?: string): string {
  return cx(
    "inline-flex items-center justify-center border font-medium transition-colors select-none",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-soft)]/60",
    "disabled:opacity-40 disabled:pointer-events-none",
    VARIANT[variant],
    SIZE[size],
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "subtle", size = "md", className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClass(variant, size, className)}
      {...rest}
    />
  );
});

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cx(
          // 16px below `sm:`, and only below: iOS Safari zooms the entire page when you
          // focus an input smaller than that, and nothing zooms it back.
          "w-full rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] px-3 py-2 text-base sm:text-sm",
          "text-zinc-100 placeholder:text-zinc-500",
          "focus:outline-none focus:border-[var(--color-accent)]/70 focus:ring-1 focus:ring-[var(--color-accent)]/40",
          className,
        )}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cx(
          "w-full appearance-none rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] px-3 py-2 text-sm",
          "text-zinc-100",
          "focus:outline-none focus:border-[var(--color-accent)]/70 focus:ring-1 focus:ring-[var(--color-accent)]/40",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-400">
          {label}
        </span>
        {hint ? <span className="text-[10px] text-zinc-500">{hint}</span> : null}
      </div>
      {children}
    </label>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  title,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  /** A real `disabled`, not a pointer-events trap: assistive tech announces it and the
   *  keyboard skips it, which "greyed out" alone does not. Callers that disable a switch
   *  owe it a `title` saying why. */
  disabled?: boolean;
  /**
   * Hover/assistive text, when the label alone does not say what the switch does.
   *
   * Composed by the CALLER, which is why this is a whole title rather than a `hint` this
   * would append: `hintTitle` lives in `components/`, and nothing in `ui/` imports from
   * there — these are leaves. Callers pass `hintTitle(label, hint)` and get the same
   * "name — explanation" wording every other control uses.
   */
  title?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] px-3 py-2 disabled:opacity-40"
    >
      <span className="truncate text-sm text-zinc-200">{label}</span>
      {/* shrink-0: an empty flex item's min-width resolves to 0, so in a tight
          row the track would collapse and the fixed-size knob would hang off it. */}
      <span
        className={cx(
          "relative h-5 w-9 shrink-0 rounded-full transition-colors",
          checked ? "bg-[var(--color-accent)]" : "bg-[var(--color-edge)]",
        )}
      >
        {/* `left` must be pinned: with only `translate-x-*` the knob rides on
            its static position, which inline layout can place anywhere in the
            track. Pinned at the inset, the travel is track − knob − 2×inset. */}
        <span
          className={cx(
            "absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform",
            checked ? "translate-x-4" : "translate-x-0",
          )}
        />
      </span>
    </button>
  );
}

export function Panel({
  title,
  right,
  children,
  className,
}: {
  title?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        "rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]",
        className,
      )}
    >
      {title ? (
        <header className="flex items-center justify-between border-b border-[var(--color-edge)] px-4 py-3">
          <h2 className="text-sm font-semibold tracking-wide text-zinc-200">{title}</h2>
          {right}
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}
